<template>
    <div>
        <b>{{ title }}</b>
        <b-card-body class="mt-2 p-0">
            <div v-if="!items" class="d-flex justify-content-center">
                <b-spinner variant="primary" label="Loading" />
            </div>

            <div v-if="items">
                <div v-if="itemsPerPage && typeof totalCount === 'number'" class="d-flex justify-content-end w-100">
                    <div class="d-flex">
                        <b-pagination
                            v-model="currentPage"
                            size="md"
                            :disabled="loading"
                            :total-rows="totalCount"
                            :per-page="itemsPerPage"
                        />

                        <b-form-input
                            v-model="searchString"
                            class="mb-3 ml-3"
                            type="text"
                            debounce="400"
                            placeholder="Search"
                            :disabled="loading"
                            style="width: unset"
                        />
                    </div>
                </div>

                <b-table
                    class="w-100 h-100 items-table"
                    table-class="h-100"
                    empty-filtered-text="No items matching your search"
                    empty-text="No items"
                    show-empty
                    striped
                    hover
                    responsive
                    :items="items || []"
                    :fields="fields"
                    :busy="loading"
                    :tbody-tr-class="getTrClass"
                    :tbody-tr-attr="getTrAttr"
                    @row-clicked="onRowClicked"
                >
                    <template v-for="(_, slotName, index) of slots" v-slot:[slotName]="scope">
                        <div :key="index" class="row-wrapper">
                            <div class="row-content">
                                <slot :name="slotName" v-bind="scope" />
                            </div>
                        </div>
                    </template>

                    <template #table-busy>
                        <div class="align-items-center d-flex justify-content-center my-2 text-center my-2">
                            <b-spinner variant="primary" />
                            <strong class="ml-3 primary-text">Loading...</strong>
                        </div>
                    </template>

                    <template #row-details="data">
                        <div class="row-wrapper">
                            <div class="row-content">
                                <slot name="row-details" :data="data" />
                            </div>
                        </div>
                    </template>
                </b-table>
            </div>
        </b-card-body>

        <b-btn v-if="showDoneButton" variant="primary" class="float-right" :disabled="!canClickDone" @click="onDone">
            {{ doneButtonText }}
        </b-btn>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, useSlots, watch } from 'vue';
import { BTable } from 'bootstrap-vue';
import { isEqual } from 'underscore';

interface GenericItem {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

const props = withDefaults(
    defineProps<{
        value?: GenericItem[] | string[];
        items?: GenericItem[];
        title?: string;
        showDoneButton?: boolean;
        doneButtonText?: string;
        fields: Record<string, string[]>[];
        loading?: boolean;
        itemsPerPage?: number;
        totalCount?: number;
        requiredSelectedItems?: number;
        idKey?: string;
        maxSelections?: number;
    }>(),
    {
        items: () => [],
        title: 'Select one or more items',
        showDoneButton: true,
        doneButtonText: 'Done',
        fields: () => [],
        requiredSelectedItems: 0,
        idKey: 'id',
        maxSelections: 0
    }
);

const emit = defineEmits([
    'input',
    'search',
    'row-clicked',
    'item-selected',
    'selection-finished',
    'details-visibility-change'
]);

const slots = useSlots();

const currentPage = ref(1);
const searchString = ref('');
let internalSelectedItems: GenericItem[] = [];
let internalSelectedIds: (string | number)[] = [];

const selectedItems = computed({
    get: () => props.value,
    set: (value) => emit('input', value)
});

const hasDetailsView = computed((): boolean => {
    return !!slots['row-details'];
});

const canClickDone = computed((): boolean => {
    return (selectedItems.value?.length || 0) >= props.requiredSelectedItems;
});

// When multi-page is active, the selected items are stored in an array of ids instead of an array of items
const isMultiPage = computed((): boolean => {
    return typeof props.itemsPerPage === 'number';
});

const selectedItemsCount = computed((): number => {
    if (isMultiPage.value) {
        return internalSelectedIds.length;
    } else {
        return internalSelectedItems.length;
    }
});

watch([searchString, currentPage], () => {
    dispatchSearch();
});

watch([selectedItems, props.items], () => {
    refreshSelectedRows();
});

onMounted(() => {
    refreshSelectedRows();
    dispatchSearch();
});

const dispatchSearch = () => {
    if (props.itemsPerPage) {
        emit('search', {
            search: searchString.value,
            from: (currentPage.value - 1) * props.itemsPerPage,
            count: props.itemsPerPage
        });
    }
};

const getTrClass = (item: GenericItem | null) => {
    if (
        item && isMultiPage.value
            ? internalSelectedIds.some((id) => id == item[props.idKey])
            : internalSelectedItems.some((i) => isEqual(i, item))
    ) {
        return ['b-table-row-selected', 'table-active'];
    } else {
        return [];
    }
};

const getTrAttr = (item: GenericItem | null) => {
    if (
        item && isMultiPage.value
            ? internalSelectedIds.some((id) => id == item[props.idKey])
            : internalSelectedItems.some((i) => isEqual(i, item))
    ) {
        return {
            'aria-selected': true
        };
    } else {
        return {
            'aria-selected': false
        };
    }
};

const onRowClicked = (clickedItem: GenericItem) => {
    if (hasDetailsView.value) {
        return;
    }

    let itemSelected = false;
    const index = isMultiPage.value
        ? internalSelectedIds.findIndex((i) => i == clickedItem[props.idKey])
        : internalSelectedItems.findIndex((i) => isEqual(i, clickedItem));

    if (index > -1) {
        // Remove the already selected item
        if (isMultiPage.value) {
            internalSelectedIds.splice(index, 1);
        } else {
            internalSelectedItems.splice(index, 1);
        }
    } else {
        // Add the selected item
        if (
            isMultiPage.value &&
            (props.maxSelections === 0 || (props.maxSelections > 0 && selectedItemsCount.value < props.maxSelections))
        ) {
            itemSelected = true;
            internalSelectedIds.push(clickedItem[props.idKey]);
        } else if (
            !isMultiPage.value &&
            (props.maxSelections === 0 || (props.maxSelections > 0 && selectedItemsCount.value < props.maxSelections))
        ) {
            itemSelected = true;
            internalSelectedItems.push(clickedItem);
        }
    }

    emit('input', isMultiPage.value ? internalSelectedIds : internalSelectedItems);
    emit('row-clicked', clickedItem);

    if (itemSelected) {
        emit('item-selected', clickedItem);
    }
};

const onDone = () => {
    emit('selection-finished', selectedItems.value);
};

const refreshSelectedRows = () => {
    if (selectedItems.value && selectedItems.value.length && props.items) {
        if (isMultiPage.value) {
            internalSelectedIds = selectedItems.value as string[];
        } else {
            internalSelectedItems = selectedItems.value as GenericItem[];
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .items-table > table > tbody > tr {
    cursor: pointer;

    & > td {
        & > .row-wrapper {
            margin: -0.75rem;
        }

        & > .row-wrapper .row-content {
            padding: 0.75rem;
        }
    }
}

::v-deep .items-table td * {
    max-height: initial !important;
}

::v-deep .items-table > table > thead {
    display: none;
}
</style>
